import { z } from 'zod';
import { Dateish } from './common';
import { DeliveryErrorSchema, DeliveryStatusSchema, WhatsappProviderSchema } from './whatsapp';

export const ChatOriginSchema = z.enum(['widget', 'whatsapp', 'manual']);
export type ChatOrigin = z.infer<typeof ChatOriginSchema>;

export const CHAT_ORIGIN_LABELS: Record<ChatOrigin, string> = {
  widget: 'Widget Externo',
  whatsapp: 'WhatsApp',
  manual: 'Manual',
};

export const ChatStatusSchema = z.enum(['inbox', 'in_attendance', 'archived']);
export type ChatStatus = z.infer<typeof ChatStatusSchema>;

/**
 * Abas da lista. As quatro primeiras são as abas do /chats (cada uma fixa um
 * `status`); `'all'` NÃO é uma aba da UI — é o modo "sem recorte de status",
 * usado pelas listas embutidas (conversas do ticket, v12), onde o histórico do
 * cliente inclui conversas encerradas. `'all'` só remove o filtro de status:
 * visibilidade por mesa e demais filtros continuam valendo.
 */
export const ChatTabSchema = z.enum(['inbox', 'mine', 'in_attendance', 'archived', 'all']);
export type ChatTab = z.infer<typeof ChatTabSchema>;

const Ref = z.object({ id: z.number().int(), name: z.string() });

/**
 * O número da empresa em que a conversa acontece (spec 028, FR-003): o canal
 * WhatsApp do `wa_phone_number_id`. null = conversa que não é de WhatsApp, ou
 * canal que já não existe. O rótulo sai de whatsappChannelLabel (whatsapp.ts).
 */
export const ChatChannelSchema = z.object({
  id: z.number().int(),
  name: z.string().nullable(),
  provider: WhatsappProviderSchema,
  displayPhone: z.string(),
});
export type ChatChannel = z.infer<typeof ChatChannelSchema>;

export const ChatListItemSchema = z.object({
  id: z.number().int(),
  origin: ChatOriginSchema,
  status: ChatStatusSchema,
  canceled: z.boolean(),
  requestorName: z.string(),
  client: Ref.nullable(),
  /** departamento do chat (v8 Etapa 4) — name = displayName da mesa */
  desk: Ref.nullable(),
  responsible: Ref.nullable(),
  /** última mensagem não-interna (preview) */
  lastMessagePreview: z.string().nullable(),
  lastMessageAt: Dateish.nullable(),
  /** mensagens do cliente após a última leitura de atendente */
  unreadCount: z.number().int(),
  /** última mensagem visível é do cliente (aguardando resposta) */
  waitingAnswer: z.boolean(),
  /**
   * Selo "Aguardando atendente" (spec 013): o chatbot desistiu (cliente
   * respondeu fora do menu duas vezes) e avisou o cliente que um humano vai
   * continuar. null = ninguém está esperando por desistência do bot; some
   * quando alguém assume ou encerra.
   */
  waitingAttendantSince: Dateish.nullable(),
  /**
   * "Com o bot" (spec 021): o canal está em modo de bot externo e a conversa
   * ainda não foi transferida — quem responde é o bot, então ela não espera a
   * equipe: fica fora do "Precisa de resposta", sem não lidas e sem o
   * "aguardando resposta". Sai no evento de transferência, ao assumir ou ao
   * encerrar.
   */
  externalBotActive: z.boolean(),
  /** por qual número da empresa a pessoa fala (spec 028, FR-003) */
  channel: ChatChannelSchema.nullable(),
  createdAt: Dateish,
});
export type ChatListItem = z.infer<typeof ChatListItemSchema>;

export const ChatDetailSchema = ChatListItemSchema.extend({
  requestorPhone: z.string().nullable(),
  requestorEmail: z.string().nullable(),
  ticketNumber: z.number().int().nullable(),
  ticketTitle: z.string().nullable(),
  assessment: z.number().int().nullable(),
  assumedAt: Dateish.nullable(),
  archivedAt: Dateish.nullable(),
  /** canal de origem do chat whatsapp (phone_number_id da Meta, v6) */
  waPhoneNumberId: z.string().nullable(),
  /** contato WhatsApp em E.164 (v6) */
  waContact: z.string().nullable(),
  /**
   * Encerramento (spec 013): motivo, observação e autor gravados no archive.
   * Conversa retomada LIMPA os três (o motivo anterior segue no histórico,
   * na mensagem de sistema do encerramento). Encerradas antes da 013 têm null.
   */
  closeReason: Ref.nullable(),
  closeNote: z.string().nullable(),
  closedBy: Ref.nullable(),
  /** chatbot ainda conduz a conversa (menu no ar / aviso fora do expediente) */
  chatbotActive: z.boolean(),
});
export type ChatDetail = z.infer<typeof ChatDetailSchema>;

/**
 * Tipo do conteúdo da mensagem (v8 Etapa 3). No banco a coluna é nulável
 * (NULL = linha anterior à v8, sempre texto); a API normaliza NULL → 'text' na
 * LEITURA, então no wire o campo é SEMPRE um destes valores — o front nunca vê
 * null. 'unknown' = tipo da Meta ainda não mapeado (renderiza como texto).
 */
export const ChatContentTypeSchema = z.enum([
  'text',
  'image',
  'audio',
  'video',
  'document',
  'sticker',
  'location',
  'unknown',
]);
export type ChatContentType = z.infer<typeof ChatContentTypeSchema>;

/** rótulos pt-BR de fallback por tipo de mídia — fonte ÚNICA (inbound Meta e
 *  upload do atendente derivam o text pela MESMA tabela, via deriveMediaText) */
export const CHAT_MEDIA_FALLBACK_LABELS: Partial<Record<ChatContentType, string>> = {
  image: '[imagem]',
  audio: '[áudio]',
  video: '[vídeo]',
  document: '[documento]',
  sticker: '[figurinha]',
};

/**
 * Regra ÚNICA do `text` de uma mensagem de mídia: caption ?? filename ??
 * rótulo pt-BR do tipo ?? '[arquivo]'. O inbound WhatsApp (process-inbound) e
 * o upload do atendente (chat-media) persistem o resultado — o histórico nunca
 * depende do arquivo para ter um texto exibível.
 */
export function deriveMediaText(
  contentType: ChatContentType,
  caption?: string | null,
  filename?: string | null
): string {
  return caption ?? filename ?? CHAT_MEDIA_FALLBACK_LABELS[contentType] ?? '[arquivo]';
}

/**
 * Quem escreveu a mensagem. 'bot' (spec 028, FR-005): a fala do bot externo
 * (Zeepchat) enquanto ele conduz a conversa — saída, sem usuário, e NÃO conta
 * como resposta da equipe (o "precisa de resposta" só olha 'attendant').
 */
export const ChatAuthorTypeSchema = z.enum(['client', 'attendant', 'system', 'bot']);
export type ChatAuthorType = z.infer<typeof ChatAuthorTypeSchema>;

export const ChatMessageSchema = z.object({
  id: z.number().int(),
  chatId: z.number().int(),
  direction: z.enum(['inbound', 'outbound']),
  authorType: ChatAuthorTypeSchema,
  authorName: z.string().nullable(),
  internal: z.boolean(),
  text: z.string(),
  /** sempre presente no wire — a API já normalizou o NULL legado para 'text' */
  contentType: ChatContentTypeSchema,
  mediaMime: z.string().nullable(),
  mediaFilename: z.string().nullable(),
  /** bytes */
  mediaSize: z.number().int().nullable(),
  /** legenda da mídia (text segue carregando o corpo/fallback) */
  caption: z.string().nullable(),
  /**
   * COMPUTADO NO SERVIDOR: caminho da rota de serving da mídia (staff
   * `/chats/:id/messages/:mid/media` ou widget
   * `/public/chat/:token/messages/:mid/media`) — o 302 para a URL presignada
   * acontece no acesso. Nunca a chave crua do storage. null = sem mídia ou
   * mídia ainda não copiada para o storage do ERP.
   */
  mediaUrl: z.string().nullable(),
  /** id Meta da mensagem (só chats whatsapp, v6) */
  wamid: z.string().nullable(),
  /** ack de entrega do outbound whatsapp (v6) */
  deliveryStatus: DeliveryStatusSchema.nullable(),
  /** com deliveryStatus 'failed': o motivo curto (spec 028, FR-006) */
  deliveryError: DeliveryErrorSchema.nullable(),
  /** quando o "Tentar de novo" já reenviou esta mensagem (a cópia é outra mensagem) */
  resentAt: Dateish.nullable(),
  createdAt: Dateish,
});
export type ChatMessage = z.infer<typeof ChatMessageSchema>;

/**
 * Outra conversa da MESMA pessoa (spec 028, FR-002): o mesmo telefone em outro
 * canal, em outra grafia (com/sem o nono dígito, com/sem o 55) ou uma conversa
 * anterior no mesmo canal. O suficiente para a barra lateral — canal, data e
 * situação — e o id para abrir.
 */
export const ChatRelatedItemSchema = z.object({
  id: z.number().int(),
  status: ChatStatusSchema,
  canceled: z.boolean(),
  requestorName: z.string(),
  /** a grafia do telefone nesta conversa (E.164 sem +) */
  waContact: z.string().nullable(),
  channel: ChatChannelSchema.nullable(),
  desk: Ref.nullable(),
  lastMessageAt: Dateish.nullable(),
  createdAt: Dateish,
});
export type ChatRelatedItem = z.infer<typeof ChatRelatedItemSchema>;

export const ChatListQuerySchema = z.object({
  tab: ChatTabSchema.default('inbox'),
  /** busca por solicitante, cliente ou departamento */
  search: z.string().optional(),
  origin: ChatOriginSchema.optional(),
  /** filtro por departamento (v8 Etapa 4) — além da visibilidade por grupos */
  desk_id: z.coerce.number().int().positive().optional(),
  /**
   * Conversas do CLIENTE (v12): chats.client_id exato. Nenhum filtro fura a
   * visibilidade por mesa — compõe (AND) com ela, como o desk_id.
   */
  client_id: z.coerce.number().int().positive().optional(),
  /** Conversas VINCULADAS a um ticket (v12): chats.ticket_number exato. */
  ticket_number: z.coerce.number().int().positive().optional(),
});
export type ChatListQuery = z.infer<typeof ChatListQuerySchema>;

export const ChatCreateBodySchema = z.object({
  requestorName: z.string().min(1),
  requestorPhone: z.string().nullish(),
  clientId: z.number().int().positive().nullish(),
  deskId: z.number().int().positive().nullish(),
  /** primeira mensagem do atendente (opcional) */
  message: z.string().nullish(),
});
export type ChatCreateBody = z.infer<typeof ChatCreateBodySchema>;

export const ChatMessageBodySchema = z.object({
  text: z.string().min(1),
  internal: z.boolean().default(false),
});
export type ChatMessageBody = z.infer<typeof ChatMessageBodySchema>;

export const ChatMessagesQuerySchema = z.object({
  after_id: z.coerce.number().int().optional(),
});

/**
 * GET /chats/:id/transcript — o arquivo da conversa (spec 013, US5). `pdf` é o
 * documento para ler e guardar; `txt`, o texto puro de sempre — e o padrão,
 * para quem já chama a rota sem `format`.
 */
export const ChatTranscriptFormatSchema = z.enum(['txt', 'pdf']);
export type ChatTranscriptFormat = z.infer<typeof ChatTranscriptFormatSchema>;

export const ChatTranscriptQuerySchema = z.object({
  format: ChatTranscriptFormatSchema.default('txt'),
});

/** legenda máxima de mídia (limite da Meta para caption) */
export const CHAT_CAPTION_MAX = 1024;

/** POST /chats/:id/template — envio de template HSM (v8 Etapa 3) */
export const ChatTemplateSendBodySchema = z.object({
  name: z.string().min(1),
  /** código de idioma da Meta (ex.: pt_BR) */
  language: z.string().min(1),
  /** valores dos placeholders {{1}}..{{n}} — o tamanho TEM que casar com o template */
  params: z.array(z.string().min(1).max(1024)).max(30).default([]),
});
export type ChatTemplateSendBody = z.infer<typeof ChatTemplateSendBodySchema>;

/** POST /chats/whatsapp — conversa ativa (v8 Etapa 3): canal + número + template */
export const ChatWhatsappCreateBodySchema = z.object({
  channelId: z.number().int().positive(),
  /** E.164 frouxo: dígitos com + opcional */
  to: z.string().regex(/^\+?[1-9]\d{7,14}$/, 'invalid_phone'),
  template: ChatTemplateSendBodySchema,
  /**
   * spec 057: aberta de dentro do ticket — a conversa nasce vinculada a ele (e ao
   * cliente dele), na mesma transação. Sem ele, herda da última encerrada.
   */
  ticketNumber: z.number().int().positive().optional(),
  /** o cliente do ticket; com o ticket, tem de ser o dele (422 ticket_not_of_client) */
  clientId: z.number().int().positive().optional(),
});
export type ChatWhatsappCreateBody = z.infer<typeof ChatWhatsappCreateBodySchema>;

/**
 * POST /chats/:id/transfer (v8 Etapa 4): EXATAMENTE um dos dois —
 * `userId` transfere para outro atendente (chat segue in_attendance) e
 * `deskId` transfere para um departamento (limpa responsável, volta ao inbox).
 * Nenhum ou ambos ⇒ 422 invalid_transfer (checado no use-case).
 */
export const ChatTransferBodySchema = z
  .object({
    userId: z.number().int().positive().optional(),
    deskId: z.number().int().positive().optional(),
  })
  .strict();
export type ChatTransferBody = z.infer<typeof ChatTransferBodySchema>;

// ── Encerramento com motivo (spec 013) ──────────────────────────────────────
export const CHAT_CLOSE_REASON_NAME_MAX = 120;
export const CHAT_CLOSE_NOTE_MAX = 2000;

/**
 * Motivos iniciais de todo tenant — a migration semeia os existentes e um
 * trigger em erp.tenants semeia os novos com ESTA lista (a migration repete os
 * textos; este array é a referência para testes e para o front).
 */
export const DEFAULT_CHAT_CLOSE_REASONS = [
  'Atendimento concluído',
  'Cliente sem retorno',
  'Encaminhado a outro setor',
  'Engano / spam',
] as const;

export const ChatCloseReasonSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  /** desativado some da escolha, mas continua nas conversas antigas */
  active: z.boolean(),
  position: z.number().int(),
});
export type ChatCloseReason = z.infer<typeof ChatCloseReasonSchema>;

export const ChatCloseReasonCreateBodySchema = z
  .object({
    name: z.string().trim().min(1).max(CHAT_CLOSE_REASON_NAME_MAX),
    active: z.boolean().default(true),
  })
  .strict();
export type ChatCloseReasonCreateBody = z.infer<typeof ChatCloseReasonCreateBodySchema>;

export const ChatCloseReasonUpdateBodySchema = z
  .object({
    name: z.string().trim().min(1).max(CHAT_CLOSE_REASON_NAME_MAX),
    active: z.boolean(),
  })
  .partial()
  .strict();
export type ChatCloseReasonUpdateBody = z.infer<typeof ChatCloseReasonUpdateBodySchema>;

/** PUT /chat/close-reasons/order — a lista COMPLETA de ids na ordem nova */
export const ChatCloseReasonReorderBodySchema = z.object({
  ids: z.array(z.number().int().positive()).min(1),
});
export type ChatCloseReasonReorderBody = z.infer<typeof ChatCloseReasonReorderBodySchema>;

/**
 * POST /chats/:id/archive. `reasonId` é OBRIGATÓRIO na regra, mas nullish no
 * schema de propósito: a ausência precisa virar `422 close_reason_required`
 * (código que a tela entende), não o `invalid_body` genérico do parse.
 * `appointment` pede o apontamento com a transcrição no ticket vinculado —
 * falha nele NÃO impede o encerramento (volta em `appointmentError`).
 */
export const ChatArchiveBodySchema = z.object({
  canceled: z.boolean().default(false),
  reasonId: z.number().int().positive().nullish(),
  note: z.string().trim().max(CHAT_CLOSE_NOTE_MAX).nullish(),
  appointment: z.boolean().default(false),
});
export type ChatArchiveBody = z.infer<typeof ChatArchiveBodySchema>;

/** resposta do archive: o detalhe + o desfecho do apontamento pedido */
export const ChatArchiveResponseSchema = ChatDetailSchema.extend({
  /** id do apontamento criado; null = não pedido, sem ticket ou falhou */
  appointmentId: z.number().int().nullable(),
  /** código do erro quando o apontamento foi pedido e não saiu */
  appointmentError: z.string().nullable(),
});
export type ChatArchiveResponse = z.infer<typeof ChatArchiveResponseSchema>;

/**
 * POST /chats/:id/assume — `takeover` = assumir de OUTRO atendente
 * (chat.manage_others); `endPause` (spec 015) = quem está em pausa escolheu
 * "Encerrar a pausa e assumir" (sem ele, 409 attendant_paused).
 */
export const ChatAssumeBodySchema = z
  .object({ takeover: z.boolean().default(false), endPause: z.boolean().default(false) })
  .strict();
export const ChatUpdateBodySchema = z
  .object({
    clientId: z.number().int().positive().nullable(),
    deskId: z.number().int().positive().nullable(),
    ticketNumber: z.number().int().positive().nullable(),
  })
  .partial()
  .strict();
export type ChatUpdateBody = z.infer<typeof ChatUpdateBodySchema>;

/** spec 056: o teto do nome do contato — o mesmo do nome que o ERP grava sozinho */
export const CHAT_REQUESTOR_NAME_MAX = 120;
export const CHAT_REQUESTOR_EMAIL_MAX = 254;
/**
 * PUT /chats/:id/contact (spec 056): o nome e o e-mail da pessoa da conversa,
 * editados no painel "Contato" do chat. O WhatsApp é o endereço da conversa e
 * não se edita. E-mail sem valor vai como null.
 */
export const ChatContactBodySchema = z
  .object({
    requestorName: z.string().trim().min(1).max(CHAT_REQUESTOR_NAME_MAX),
    requestorEmail: z.string().trim().max(CHAT_REQUESTOR_EMAIL_MAX).email().nullable(),
  })
  .strict();
export type ChatContactBody = z.infer<typeof ChatContactBodySchema>;

// ── Configuração do widget (v11 §2): GET/PUT /chat/settings ─────────────────
/** caps de tamanho dos textos do widget — API valida, UI mostra os limites */
export const CHAT_WIDGET_TITLE_MAX = 120;
export const CHAT_WIDGET_GREETING_MAX = 1024;

/**
 * Singleton por tenant (find-or-default: sem linha ⇒ enabled=true, resto null).
 * `deskId` é a mesa que recebe os chats novos do widget; `greeting` vira a
 * primeira mensagem (de sistema) da conversa; `title` é o header do widget.
 */
export const ChatSettingsSchema = z.object({
  enabled: z.boolean(),
  deskId: z.number().int().nullable(),
  greeting: z.string().nullable(),
  title: z.string().nullable(),
  /** slug do tenant — o snippet de embed monta /chat?tenant=<slug> com ele */
  tenantSlug: z.string(),
});
export type ChatSettings = z.infer<typeof ChatSettingsSchema>;

export const ChatSettingsPutBodySchema = z.object({
  enabled: z.boolean(),
  deskId: z.number().int().positive().nullable(),
  greeting: z.string().trim().min(1).max(CHAT_WIDGET_GREETING_MAX).nullable(),
  title: z.string().trim().min(1).max(CHAT_WIDGET_TITLE_MAX).nullable(),
});
export type ChatSettingsPutBody = z.infer<typeof ChatSettingsPutBodySchema>;

/**
 * Payload PÚBLICO do widget (GET /public/chat/config, tenant por X-Tenant ou
 * ?tenant=): o widget lê título/saudação/logo antes de abrir conversa e
 * renderiza o estado desligado sem tentar o start. `logoUrl` é uma URL
 * presignada de curta duração da logo da organização (v11 §1) — best-effort:
 * null = sem logo (ou storage indisponível), como hoje.
 */
export const PublicWidgetConfigSchema = z.object({
  enabled: z.boolean(),
  title: z.string().nullable(),
  greeting: z.string().nullable(),
  logoUrl: z.string().nullable(),
});
export type PublicWidgetConfig = z.infer<typeof PublicWidgetConfigSchema>;

// ── Widget público (sem auth) ───────────────────────────────────────────────
export const PublicChatStartBodySchema = z.object({
  name: z.string().min(1),
  phone: z.string().nullish(),
});
export const PublicChatStartResponseSchema = z.object({ token: z.string() });

export const PublicChatStateSchema = z.object({
  status: ChatStatusSchema,
  canceled: z.boolean(),
  responsibleName: z.string().nullable(),
  assessment: z.number().int().nullable(),
  /** mensagens visíveis ao cliente (nunca internas) */
  messages: z.array(ChatMessageSchema),
});
export type PublicChatState = z.infer<typeof PublicChatStateSchema>;

export const PublicChatMessageBodySchema = z.object({ text: z.string().min(1) });
export const PublicChatRateBodySchema = z.object({ assessment: z.number().int().min(1).max(5) });
